import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './EquipmentConsultProductionPeriodPicker.css';

export const MAX_PRODUCTION_PERIOD_DAYS = 370;

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getDefaultProductionPeriod() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

export function getCalendarBounds() {
  const today = new Date();
  const min = new Date(today.getFullYear() - 10, 0, 1);
  return { minDate: toYmd(min), maxDate: toYmd(today) };
}

function daysInclusiveYmd(startYmd, endYmd) {
  const [ys, ms, ds] = startYmd.split('-').map((x) => parseInt(x, 10));
  const [ye, me, de] = endYmd.split('-').map((x) => parseInt(x, 10));
  const a = new Date(ys, ms - 1, ds);
  const b = new Date(ye, me - 1, de);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.floor((b - a) / 86400000) + 1;
}

export function formatPickerDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const leading = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  const prevMonthDate = new Date(year, month, 0);
  const prevDays = prevMonthDate.getDate();
  const prevMonth = prevMonthDate.getMonth();
  const prevYear = prevMonthDate.getFullYear();
  for (let i = leading - 1; i >= 0; i -= 1) {
    const day = prevDays - i;
    cells.push({ year: prevYear, month: prevMonth, day, outside: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ year, month, day, outside: false });
  }

  let nextDay = new Date(year, month + 1, 1);
  while (cells.length < 42) {
    cells.push({
      year: nextDay.getFullYear(),
      month: nextDay.getMonth(),
      day: nextDay.getDate(),
      outside: true,
    });
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return cells;
}

function MonthPanel({
  year,
  month,
  minDate,
  maxDate,
  startDate,
  endDate,
  onSelect,
  showPrev,
  showNext,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  markedDates,
}) {
  const cells = useMemo(() => buildMonthCells(year, month), [year, month]);
  const monthLabel = new Date(year, month, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  const todayStr = toYmd(new Date());
  const marks = useMemo(() => {
    if (!markedDates) return null;
    if (markedDates instanceof Set) return markedDates;
    return new Set(Array.isArray(markedDates) ? markedDates : []);
  }, [markedDates]);

  return (
    <div className="ec-period-month">
      <div className="ec-period-month-head">
        {showPrev ? (
          <button
            type="button"
            className="ec-period-nav"
            onClick={onPrev}
            disabled={prevDisabled}
            aria-label="Mês anterior"
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
        ) : (
          <span className="ec-period-nav-spacer" aria-hidden />
        )}
        <span className="ec-period-month-title">{monthLabel}</span>
        {showNext ? (
          <button
            type="button"
            className="ec-period-nav"
            onClick={onNext}
            disabled={nextDisabled}
            aria-label="Próximo mês"
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        ) : (
          <span className="ec-period-nav-spacer" aria-hidden />
        )}
      </div>
      <div className="ec-period-weekdays">
        {WEEKDAYS.map((w) => (
          <span key={w} className="ec-period-weekday">
            {w}
          </span>
        ))}
      </div>
      <div className="ec-period-days">
        {cells.map((cell) => {
          const dateStr = toYmd(new Date(cell.year, cell.month, cell.day));
          const disabled = dateStr < minDate || dateStr > maxDate;
          const inRange = startDate && endDate && dateStr >= startDate && dateStr <= endDate;
          const isStart = startDate && dateStr === startDate;
          const isEnd = endDate && dateStr === endDate;
          const hasMark = !!(marks && marks.has(dateStr));
          const classes = [
            'ec-period-day',
            cell.outside ? 'ec-period-day--outside' : '',
            disabled ? 'ec-period-day--disabled' : '',
            inRange ? 'ec-period-day--in-range' : '',
            isStart ? 'ec-period-day--range-start ec-period-day--selected-start' : '',
            isEnd ? 'ec-period-day--range-end ec-period-day--selected-end' : '',
            dateStr === todayStr ? 'ec-period-day--today' : '',
            hasMark ? 'ec-period-day--has-mark' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={`${dateStr}-${cell.outside ? 'o' : 'i'}`}
              type="button"
              className={classes}
              disabled={disabled}
              onClick={() => !disabled && onSelect(dateStr)}
            >
              <span className="ec-period-day-inner">{cell.day}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangeCalendarDual({ minDate, maxDate, startDate, endDate, onDateSelect, markedDates }) {
  const [viewMonth, setViewMonth] = useState(() => {
    if (startDate) {
      const [y, m] = startDate.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  const monthLeft = viewMonth;
  const monthRight = useMemo(
    () => new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
    [viewMonth]
  );

  const handleDateClick = (dateStr) => {
    if (!startDate || (startDate && endDate)) {
      onDateSelect(dateStr, null);
    } else if (dateStr < startDate) {
      onDateSelect(dateStr, startDate);
    } else {
      onDateSelect(startDate, dateStr);
    }
  };

  const minMonth = useMemo(() => {
    const [y, m] = minDate.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  }, [minDate]);

  const maxMonth = useMemo(() => {
    const [y, m] = maxDate.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  }, [maxDate]);

  const canPrev =
    viewMonth.getFullYear() > minMonth.getFullYear()
    || (viewMonth.getFullYear() === minMonth.getFullYear() && viewMonth.getMonth() > minMonth.getMonth());

  const rightLimit = new Date(maxMonth.getFullYear(), maxMonth.getMonth() - 1, 1);
  const canNext =
    viewMonth.getFullYear() < rightLimit.getFullYear()
    || (viewMonth.getFullYear() === rightLimit.getFullYear() && viewMonth.getMonth() < rightLimit.getMonth());

  return (
    <div className="ec-period-picker-calendars">
      <MonthPanel
        year={monthLeft.getFullYear()}
        month={monthLeft.getMonth()}
        minDate={minDate}
        maxDate={maxDate}
        startDate={startDate}
        endDate={endDate}
        markedDates={markedDates}
        onSelect={handleDateClick}
        showPrev
        showNext={false}
        onPrev={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
        prevDisabled={!canPrev}
      />
      <MonthPanel
        year={monthRight.getFullYear()}
        month={monthRight.getMonth()}
        minDate={minDate}
        maxDate={maxDate}
        startDate={startDate}
        endDate={endDate}
        markedDates={markedDates}
        onSelect={handleDateClick}
        showPrev={false}
        showNext
        onNext={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
        nextDisabled={!canNext}
      />
    </div>
  );
}

export function RangeCalendarSingle({ minDate, maxDate, startDate, endDate, onDateSelect, dayMode = false, initialMonthDate, markedDates }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const seed = startDate || initialMonthDate;
    if (seed) {
      const [y, m] = seed.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    }
    if (maxDate) {
      const [y, m] = maxDate.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  const handleDateClick = (dateStr) => {
    if (dayMode) {
      onDateSelect(dateStr, dateStr);
      return;
    }
    if (!startDate || (startDate && endDate)) {
      onDateSelect(dateStr, null);
    } else if (dateStr < startDate) {
      onDateSelect(dateStr, startDate);
    } else {
      onDateSelect(startDate, dateStr);
    }
  };

  const minMonth = useMemo(() => {
    const [y, m] = minDate.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  }, [minDate]);

  const maxMonth = useMemo(() => {
    const [y, m] = maxDate.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  }, [maxDate]);

  const canPrev =
    viewMonth.getFullYear() > minMonth.getFullYear()
    || (viewMonth.getFullYear() === minMonth.getFullYear() && viewMonth.getMonth() > minMonth.getMonth());

  const canNext =
    viewMonth.getFullYear() < maxMonth.getFullYear()
    || (viewMonth.getFullYear() === maxMonth.getFullYear() && viewMonth.getMonth() < maxMonth.getMonth());

  return (
    <div className="ec-period-picker-calendars ec-period-picker-calendars--single">
      <MonthPanel
        year={viewMonth.getFullYear()}
        month={viewMonth.getMonth()}
        minDate={minDate}
        maxDate={maxDate}
        startDate={startDate}
        endDate={endDate}
        markedDates={markedDates}
        onSelect={handleDateClick}
        showPrev
        showNext
        onPrev={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
        onNext={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
        prevDisabled={!canPrev}
        nextDisabled={!canNext}
      />
    </div>
  );
}

export function PeriodModal({
  anchorRef,
  anchorRect,
  currentPeriod,
  onApply,
  onClear,
  onClose,
  minDate: minDateProp,
  maxDate: maxDateProp,
  maxRangeDays = MAX_PRODUCTION_PERIOD_DAYS,
  title = 'Filtrar por período',
  hideHeader = false,
  modalClassName = '',
  zIndex = 10010,
  singleMonth = false,
  dayMode = false,
  hideActionsBorder = false,
  useUserFormActions = false,
  initialMonthDate = null,
  markedDates = null,
}) {
  const modalRef = useRef(null);
  const calendarBounds = getCalendarBounds();
  const minDate = minDateProp ?? calendarBounds.minDate;
  const maxDate = maxDateProp ?? calendarBounds.maxDate;
  const [startDate, setStartDate] = useState(currentPeriod?.startDate || null);
  const [endDate, setEndDate] = useState(
    dayMode
      ? currentPeriod?.startDate || currentPeriod?.endDate || null
      : currentPeriod?.endDate || null
  );
  const [rangeError, setRangeError] = useState('');
  const [position, setPosition] = useState(() => {
    if (!anchorRect || typeof window === 'undefined') return null;
    const popoverWidth = Math.min(singleMonth || dayMode ? 320 : 580, window.innerWidth - 24);
    let left = anchorRect.left;
    if (left + popoverWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - popoverWidth - 12);
    }
    const spaceBelow = window.innerHeight - anchorRect.bottom - 12;
    if (spaceBelow >= 280) {
      return { top: anchorRect.bottom + 8, left, bottom: 'auto', right: 'auto' };
    }
    return {
      bottom: window.innerHeight - anchorRect.top + 8,
      right: Math.max(12, window.innerWidth - anchorRect.right),
      top: 'auto',
      left: 'auto',
    };
  });

  useLayoutEffect(() => {
    const popoverWidth = Math.min(singleMonth || dayMode ? 320 : 580, window.innerWidth - 24);
    if (anchorRect) {
      let left = anchorRect.left;
      if (left + popoverWidth > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - popoverWidth - 12);
      }
      const spaceBelow = window.innerHeight - anchorRect.bottom - 12;
      if (spaceBelow >= 280) {
        setPosition({ top: anchorRect.bottom + 8, left, bottom: 'auto', right: 'auto' });
      } else {
        setPosition({
          bottom: window.innerHeight - anchorRect.top + 8,
          right: Math.max(12, window.innerWidth - anchorRect.right),
          top: 'auto',
          left: 'auto',
        });
      }
      return;
    }
    const el = anchorRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let right = window.innerWidth - rect.right;
    if (rect.right - popoverWidth < 12) {
      right = Math.max(12, window.innerWidth - rect.left - popoverWidth);
    }
    setPosition({
      bottom: window.innerHeight - rect.top + 8,
      right,
      top: 'auto',
      left: 'auto',
    });
  }, [anchorRef, anchorRect, singleMonth, dayMode]);

  useEffect(() => {
    const onDoc = (e) => {
      if (modalRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      if (e.target.closest?.('.ec-period-nav')) return;
      if (e.target.closest?.('[data-tpm="map-track-date"]')) return;
      if (e.target.closest?.('[data-oosv2-future-date]')) return;
      if (e.target.closest?.('.futura-date-filter-trigger')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [anchorRef, onClose]);

  const rangeLabel = useMemo(() => {
    if (dayMode) {
      if (startDate) return formatPickerDate(startDate);
      return 'Selecione a data';
    }
    if (startDate && endDate) {
      return `${formatPickerDate(startDate)} — ${formatPickerDate(endDate)}`;
    }
    if (startDate) {
      return `${formatPickerDate(startDate)} — …`;
    }
    return 'Selecione o intervalo de datas';
  }, [startDate, endDate, dayMode]);

  const handleApply = () => {
    if (dayMode) {
      if (!startDate) return;
      setRangeError('');
      onApply(startDate, startDate);
      onClose();
      return;
    }
    if (!startDate || !endDate || startDate > endDate) return;
    const days = daysInclusiveYmd(startDate, endDate);
    if (days > maxRangeDays) {
      setRangeError(`Selecione no máximo ${maxRangeDays} dias.`);
      return;
    }
    setRangeError('');
    onApply(startDate, endDate);
    onClose();
  };

  if (!position) return null;

  const style = {
    position: 'fixed',
    zIndex,
    '--oosv2-future-date-z': zIndex,
    width: Math.min(singleMonth || dayMode ? 320 : 580, window.innerWidth - 24),
    ...(position.top != null && position.top !== 'auto' ? { top: `${position.top}px` } : { top: 'auto' }),
    ...(position.bottom != null && position.bottom !== 'auto'
      ? { bottom: `${position.bottom}px` }
      : { bottom: 'auto' }),
    ...(position.left != null && position.left !== 'auto'
      ? { left: `${position.left}px` }
      : { left: 'auto' }),
    ...(position.right != null && position.right !== 'auto'
      ? { right: `${position.right}px` }
      : { right: 'auto' }),
  };

  const applyDisabled = dayMode
    ? !startDate
    : !startDate || !endDate || startDate > endDate;

  const clearBtnClass = useUserFormActions
    ? 'user-form-link-btn user-form-footer-btn'
    : 'odv2-pill-btn odv2-pill-btn--ghost';
  const applyBtnClass = useUserFormActions
    ? 'user-form-profile-save-btn user-form-footer-btn'
    : 'odv2-pill-btn odv2-pill-btn--primary';

  return ReactDOM.createPortal(
    <div
      ref={modalRef}
      className={`equipment-consult-period-modal${hideHeader ? ' equipment-consult-period-modal--no-head' : ''}${hideActionsBorder ? ' equipment-consult-period-modal--no-actions-border' : ''}${modalClassName ? ` ${modalClassName}` : ''}`}
      style={style}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {!hideHeader ? (
        <div className="ec-period-picker-head">
          <div>
            <p className="ec-period-picker-title">{title}</p>
            <p
              className={`ec-period-picker-range${!startDate ? ' ec-period-picker-range--empty' : ''}`}
            >
              {rangeLabel}
            </p>
          </div>
          <button type="button" className="ec-period-picker-close" onClick={onClose} aria-label="Fechar">
            <X size={16} strokeWidth={2} />
          </button>
        </div>
      ) : null}
      <div className="ec-period-picker-body">
        {singleMonth || dayMode ? (
          <RangeCalendarSingle
            minDate={minDate}
            maxDate={maxDate}
            startDate={startDate}
            endDate={endDate}
            dayMode={dayMode}
            initialMonthDate={initialMonthDate}
            markedDates={markedDates}
            onDateSelect={(s, e) => {
              setStartDate(s);
              setEndDate(e);
              setRangeError('');
            }}
          />
        ) : (
          <RangeCalendarDual
            minDate={minDate}
            maxDate={maxDate}
            startDate={startDate}
            endDate={endDate}
            markedDates={markedDates}
            onDateSelect={(s, e) => {
              setStartDate(s);
              setEndDate(e);
              setRangeError('');
            }}
          />
        )}
        {rangeError ? <p className="ec-period-picker-error">{rangeError}</p> : null}
      </div>
      <div className="ec-period-picker-actions">
        <button
          type="button"
          className={clearBtnClass}
          onClick={() => {
            onClear();
            onClose();
          }}
        >
          Limpar
        </button>
        <button
          type="button"
          className={applyBtnClass}
          disabled={applyDisabled}
          onClick={handleApply}
        >
          Aplicar
        </button>
      </div>
    </div>,
    document.body
  );
}

export default function EquipmentConsultProductionPeriodPicker({
  currentPeriod,
  onApply,
  onClear,
  disabled = false,
}) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="equipment-consult-chart-period-btn"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title="Analisar outro período"
      >
        <Calendar size={14} strokeWidth={2} aria-hidden />
        Analisar outro período
      </button>
      {open && (
        <PeriodModal
          anchorRef={btnRef}
          currentPeriod={currentPeriod}
          onApply={onApply}
          onClear={onClear}
          onClose={close}
        />
      )}
    </>
  );
}
